import { Popover, Tooltip } from "@xola/ui-kit";
import { PurchaseItemStatus } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Experience, ExperiencePurchaseItem, Guest, GuestStatus, Purchase } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { useUpdateEffect } from "../../../hooks/useUpdateEffect";
import { useVisibility } from "../../../hooks/useVisibility";
import { CheckInButton } from "./CheckInButton";
import { CheckInButtonWithSummary } from "./CheckInButtonWithSummary";
import { getCheckInStatusInfo, isItemLevelCheckIn } from "./CheckInHelper";
import { CheckInPurchaseGuestList } from "./CheckInPurchaseGuestList";

interface SaveGuestStatusPayload {
    guestStatus: GuestStatus;
    guests: Guest[];
}

interface CheckInQuickSearchGuestMenuProps {
    purchase: Purchase;
    purchaseItem: ExperiencePurchaseItem;
    onOpenChange?: (isOpen: boolean) => void;
}

export const CheckInQuickSearchGuestMenu = ({
    purchase,
    purchaseItem,
    onOpenChange,
}: CheckInQuickSearchGuestMenuProps) => {
    const invalidate = useInvalidate();
    const seller = useSeller();

    const { data: experience } = useResource<Experience>(["products", purchaseItem.product.id]);

    const isCheckInEnabled = !!seller?.preferences.checkIn?.enabled || experience?.checkInPreference?.enabled;

    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const [showMenu, { toggle: toggleMenu, hide: hideMenu }] = useVisibility(false);
    const [openedVia, setOpenedVia] = useState<string | null>(null);
    const [shouldRenderPopover, { show: renderPopoverContent, hide: hidePopoverContent }] = useVisibility(false);

    const { isCheckedIn, isReturned, areGuestsCheckedInOrReturnedOnly } = getCheckInStatusInfo(purchaseItem);

    const isEarlyReturnEnabled = experience?.earlyReturn ?? false;
    const showEquipmentReturn = (isReturned || isCheckedIn || areGuestsCheckedInOrReturnedOnly) && isEarlyReturnEnabled;

    const { mutateAsync: saveGuestStatus, isLoading } = useMutation(
        async ({ guestStatus, guests }: SaveGuestStatusPayload) => {
            // Equipment without collect-demographics has no guests; the backend applies check-in status at the
            // item level via items[].guestStatus (see ExperiencePurchaseItem::hydrate). XS-1821.
            const itemPayload = isItemLevelCheckIn(purchaseItem)
                ? { id: purchaseItem.id, guestStatus }
                : { id: purchaseItem.id, guests: guests.map((guest) => ({ id: guest.id, guestStatus })) };
            await api.patch(`/purchases/${purchase.id}`, { items: [itemPayload] });
        },
    );

    useUpdateEffect(() => {
        onOpenChange?.(showMenu);
    }, [showMenu]);

    const handleSubMenuOpenChange = (isSubMenuOpen: boolean) => {
        setIsSubMenuOpen(isSubMenuOpen);
    };

    const handleGroupReturnClick = async () => {
        const guestStatus = isReturned ? GuestStatus.ARRIVED : GuestStatus.RETURNED;
        const { guests } = purchaseItem;
        handleGuestStatusChange(guestStatus, guests);
    };

    const handleGuestStatusChange = async (guestStatus: GuestStatus, guests: Guest[]) => {
        await saveGuestStatus({ guestStatus, guests });
        invalidate("purchases", purchase.id);
        invalidate("purchaseItems", purchaseItem.id);
    };

    /**
     * This is needed to handle case when the user switches between check-in and no-show,
     * since these actions open same modal, we don't need to close it after the user changes action.
     */
    const handleSummaryButtonClick = (button: "checkIn" | "noShow") => {
        if (!openedVia) {
            toggleMenu();
            setOpenedVia(button);
        } else if (openedVia === button) {
            toggleMenu();
            setOpenedVia(null);
        } else {
            setOpenedVia(button);
        }
    };

    return isCheckInEnabled ? (
        <Popover
            appendTo={document.body}
            maxWidth={400}
            offset={[0, 10]}
            placement="bottom"
            zIndex={30}
            visible={showMenu}
            // Do not render popover content when instance is created.
            onCreate={() => hidePopoverContent()}
            // Renders popover content only when its mounted to the DOM.
            onMount={() => renderPopoverContent()}
            // Removes when unmounted.
            onHidden={() => hidePopoverContent()}
            onClickOutside={() => {
                if (!isSubMenuOpen) {
                    toggleMenu();
                    setOpenedVia(null);
                }
            }}
        >
            {[PurchaseItemStatus.PENDING_ACCEPT, PurchaseItemStatus.PENDING_MINIMUM].includes(
                purchaseItem.status,
            ) ? null : (
                <span>
                    <CheckInButtonWithSummary
                        purchaseItem={purchaseItem}
                        isLoading={isLoading}
                        disabled={isLoading || isReturned}
                        action="checkIn"
                        onMouseDown={() => handleSummaryButtonClick("checkIn")}
                    />

                    {showEquipmentReturn ? (
                        <Tooltip content="Return Equipment Early">
                            <CheckInButton
                                isDisabled={isLoading || showMenu}
                                isLoading={isLoading}
                                active={isReturned}
                                action="return"
                                onClick={() => {
                                    hideMenu();
                                    handleGroupReturnClick();
                                }}
                            />
                        </Tooltip>
                    ) : (
                        <CheckInButtonWithSummary
                            purchaseItem={purchaseItem}
                            isLoading={isLoading}
                            disabled={isLoading}
                            action="noShow"
                            onMouseDown={() => handleSummaryButtonClick("noShow")}
                        />
                    )}
                </span>
            )}

            <Popover.Content className="w-96">
                {/* Per guest listing of guests */}
                {shouldRenderPopover && (
                    <CheckInPurchaseGuestList
                        purchase={purchase}
                        purchaseItem={purchaseItem}
                        onSubMenuOpenChange={handleSubMenuOpenChange}
                    />
                )}
            </Popover.Content>
        </Popover>
    ) : null;
};
